import { useLoanLock } from '@src/providers/LoanLockProvider'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import FeesDialog from './EditFees/FeesDialog'
import EditPaymentsDialog, { EEditPaymentsSubAction } from './EditPayments/EditPaymentsDialog'
import LoanCalendarFrequencyDialog from './LoanCalendarFrequency/LoanCalendarFrequencyDialog'
import LoanCalendarPaymentStatusDialog from './LoanCalendarPaymentStatus/LoanCalendarPaymentStatusDialog'
import LoanCalendarResetDialog from './LoanCalendarResetDialog'
import { useLoanCalendarSimulation } from './LoanCalendarSimulationProvider'
import LoanCalendarChangeRegularPmtDialog from './LoanCalendarChangeRegularPmt/LoanCalendarChangeRegularPmtDialog'
import LoanMoveCalendarDialog from './LoanMoveCalendar/LoanMoveCalendarDialog'

export enum ELoanCalendarAction {
  OpenUpdatePaymentFrequencyDialog = 'openUpdatePaymentFrequencyDialog',
  OpenMoveCalendarDialog = 'openMoveCalendarDialog',
  OpenResetPaymentCalendarDialog = 'openResetPaymentCalendarDialog',
  OpenEditPaymentsDialog = 'openEditPaymentsDialog',
  OpenAddRecurringPaymentsDialog = 'openAddRecurringPaymentsDialog',
  OpenSpreadOverLateAmountDialog = 'openSpreadOverLateAmountDialog',
  OpenAddPaymentDialog = 'openAddPaymentDialog',
  OpenDeletePaymentsDialog = 'openDeletePaymentsDialog',
  OpenCatchUpAmountOfOverdueForDate = 'openCatchUpAmountOfOverdueForDate',
  OpenChangePaymentStatusDialog = 'openChangePaymentStatusDialog',
  OpenMoveSelectedPaymentsDialog = 'openMoveSelectedPaymentsDialog',
  OpenAmountToReplaceForDate = 'openAmountToReplaceForDate',
  OpenBatchUpdatePaymentsDialog = 'openBatchUpdatePaymentsDialog',
  OpenIncreasePaymentsDialog = 'openIncreasePaymentsDialog',
  OpenDecreasePaymentsDialog = 'openDecreasePaymentsDialog',
  OpenCatchUpOnRemainingPaymentsDialog = 'openCatchUpOnRemainingPaymentsDialog',
  OpenChangeFeeAmountsDialog = 'openChangeFeeAmountsDialog',
  OpenChangeRegularPmtDialog = 'openChangeRegularPmtDialog',
}

const editPaymentsActions = new Set<ELoanCalendarAction>([
  ELoanCalendarAction.OpenEditPaymentsDialog,
  ELoanCalendarAction.OpenAddRecurringPaymentsDialog,
  ELoanCalendarAction.OpenAddPaymentDialog,
  ELoanCalendarAction.OpenDeletePaymentsDialog,
  ELoanCalendarAction.OpenCatchUpAmountOfOverdueForDate,
  ELoanCalendarAction.OpenSpreadOverLateAmountDialog,
  ELoanCalendarAction.OpenMoveSelectedPaymentsDialog,
  ELoanCalendarAction.OpenAmountToReplaceForDate,
  ELoanCalendarAction.OpenBatchUpdatePaymentsDialog,
  ELoanCalendarAction.OpenIncreasePaymentsDialog,
  ELoanCalendarAction.OpenDecreasePaymentsDialog,
  ELoanCalendarAction.OpenCatchUpOnRemainingPaymentsDialog,
])

const editPaymentsSubAction = new Map<ELoanCalendarAction, EEditPaymentsSubAction>([
  [ELoanCalendarAction.OpenAddRecurringPaymentsDialog, EEditPaymentsSubAction.AddRecurringPayments],
  [ELoanCalendarAction.OpenAddPaymentDialog, EEditPaymentsSubAction.AddPayment],
  [ELoanCalendarAction.OpenDeletePaymentsDialog, EEditPaymentsSubAction.DeletePayment],
  [ELoanCalendarAction.OpenSpreadOverLateAmountDialog, EEditPaymentsSubAction.SpreadLateAmount],
  [ELoanCalendarAction.OpenCatchUpAmountOfOverdueForDate, EEditPaymentsSubAction.CatchUpAmountOfOverdueForDate],
  [ELoanCalendarAction.OpenMoveSelectedPaymentsDialog, EEditPaymentsSubAction.MovePayments],
  [ELoanCalendarAction.OpenBatchUpdatePaymentsDialog, EEditPaymentsSubAction.BatchUpdatePayments],
  [ELoanCalendarAction.OpenIncreasePaymentsDialog, EEditPaymentsSubAction.BatchUpdatePayments],
  [ELoanCalendarAction.OpenDecreasePaymentsDialog, EEditPaymentsSubAction.BatchUpdatePayments],
  [ELoanCalendarAction.OpenCatchUpOnRemainingPaymentsDialog, EEditPaymentsSubAction.BatchUpdatePayments],
])

interface ILoanCalendarActionContext {
  action: ELoanCalendarAction | null
  dispatchAction: (action: ELoanCalendarAction | null) => void
  selectedPaymentIds: Set<string>
  setSelectedPaymentIds: (payments: Set<string>) => void
  clearSelection: () => void
}

const LoanCalendarActionContext = createContext<ILoanCalendarActionContext | null>(null)

export const useLoanCalendarAction = () => {
  const ctx = useContext(LoanCalendarActionContext)
  if (!ctx) throw new Error('useLoanCalendarAction must be used within a LoanCalendarActionProvider')
  return ctx
}

interface LoanCalendarActionProviderProps {
  children: ReactNode
}

const LoanCalendarActionProvider = ({ children }: LoanCalendarActionProviderProps) => {
  const { originalSimulation, currentSimulation, addSimulationAction } = useLoanCalendarSimulation()
  const { isReadOnly } = useLoanLock()
  const [action, setAction] = useState<ELoanCalendarAction | null>(null)
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<Set<string>>(new Set())
  const navigate = useNavigate()
  const location = useLocation()

  const handleActionClose = useCallback(() => setAction(null), [])

  const clearSelection = useCallback(() => {
    setSelectedPaymentIds(new Set())
  }, [])

  const ctx = useMemo(
    () => ({
      action,
      dispatchAction: setAction,
      selectedPaymentIds,
      setSelectedPaymentIds,
      clearSelection,
    }),
    [action, selectedPaymentIds, clearSelection],
  )

  const handleAddSimulationAction = useCallback(
    (action: SignedCalendarSimulation) => {
      addSimulationAction(action)
    },
    [addSimulationAction],
  )

  const closeDialogAndClearSelection = useCallback(() => {
    setAction(null)
    clearSelection()
  }, [clearSelection])

  const handleEditPaymentsClose = useCallback(() => {
    closeDialogAndClearSelection()
    navigate(-1)
  }, [closeDialogAndClearSelection, navigate])

  const isOpenEditPaymentsDialog = useMemo(() => {
    if (action && editPaymentsActions.has(action)) {
      navigate(location.pathname + location.search, {
        state: { editPaymentDialog: true },
        replace: false,
      })
      return true
    }
    return false
  }, [action, location.pathname, location.search, navigate])

  useEffect(() => {
    const handlePopState = () => {
      if (isOpenEditPaymentsDialog) {
        closeDialogAndClearSelection()
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [closeDialogAndClearSelection, isOpenEditPaymentsDialog])

  return (
    <>
      <LoanCalendarActionContext.Provider value={ctx}>{children}</LoanCalendarActionContext.Provider>

      {/* Made to centralize all dialogs. Add other dialogs here as needed  */}
      {action === ELoanCalendarAction.OpenUpdatePaymentFrequencyDialog && (
        <LoanCalendarFrequencyDialog
          initialSignedSimulation={currentSimulation}
          onConfirm={handleAddSimulationAction}
          onClose={handleActionClose}
          isLockedByOther={isReadOnly}
        />
      )}
      {action === ELoanCalendarAction.OpenChangeRegularPmtDialog && (
        <LoanCalendarChangeRegularPmtDialog
          initialSignedSimulation={currentSimulation}
          onConfirm={handleAddSimulationAction}
          onClose={handleActionClose}
          isLockedByOther={isReadOnly}
        />
      )}
      {action === ELoanCalendarAction.OpenMoveCalendarDialog && (
        <LoanMoveCalendarDialog
          initialSignedSimulation={currentSimulation}
          onConfirm={handleAddSimulationAction}
          onClose={handleActionClose}
          isLockedByOther={isReadOnly}
        />
      )}
      {action === ELoanCalendarAction.OpenResetPaymentCalendarDialog && (
        <LoanCalendarResetDialog
          originalSignedSimulation={originalSimulation}
          onConfirm={handleAddSimulationAction}
          onClose={handleActionClose}
          isLockedByOther={isReadOnly}
        />
      )}
      {action === ELoanCalendarAction.OpenChangePaymentStatusDialog && (
        <LoanCalendarPaymentStatusDialog
          paymentIds={selectedPaymentIds}
          initialSignedSimulation={currentSimulation}
          onConfirm={handleAddSimulationAction}
          onClose={closeDialogAndClearSelection}
          isLockedByOther={isReadOnly}
        />
      )}
      {action === ELoanCalendarAction.OpenChangeFeeAmountsDialog && (
        <FeesDialog
          initialSignedSimulation={currentSimulation}
          onClose={closeDialogAndClearSelection}
          onConfirm={handleAddSimulationAction}
          isLockedByOther={isReadOnly}
        />
      )}
      {action && editPaymentsActions.has(action) && (
        <EditPaymentsDialog
          initialSignedSimulation={currentSimulation}
          selectedPaymentIds={selectedPaymentIds}
          onConfirm={handleAddSimulationAction}
          onClose={handleEditPaymentsClose}
          defaultSubAction={editPaymentsSubAction.get(action) ?? undefined}
          isCatchUpMode={action === ELoanCalendarAction.OpenCatchUpOnRemainingPaymentsDialog}
          isIncreasingPayments={action !== ELoanCalendarAction.OpenDecreasePaymentsDialog}
          isLockedByOther={isReadOnly}
        />
      )}
    </>
  )
}

export default LoanCalendarActionProvider
